import { Feature_Config, Feature_Type_Config, Feature_Value, Kap_Measure, Maybe } from 'src/@types/graphql'
import { LEVEL } from 'src/shared/constants/constants'

/**
 * Gets the selected features that deviate from the features selected in the related factsheet.
 *
 * @param selectedFeatures features that are selected in the related entity (Project or KAP measure)
 * @param factsheetFeatures features that are selected in the Factsheet
 * @returns an array of invalid feature values
 */
const getInvalidSelectedFeatures = (
  selectedFeatures: Feature_Value[],
  factsheetFeatures: Feature_Value[],
): Feature_Value[] => {
  return selectedFeatures.filter(
    (feature) =>
      factsheetFeatures
        .map((factsheetFeature) => factsheetFeature.feature_config.id)
        .indexOf(feature.feature_config.id) < 0,
  )
}

/**
 * Validates if a feature type has invalid feature selection.
 *
 * @param featureTypeConfig feature type config data for the feature type being validated
 * @param kapMeasure entity related to the feature base
 * @returns true if the feature type is invalid
 */
const isFeatureTypeWithInvalidSelections = (
  featureTypeConfig: Feature_Type_Config,
  kapMeasure: Kap_Measure | undefined,
): boolean => {
  let selectedFeatures = kapMeasure?.feature_base?.feature_values ?? []
  const factsheetFeatures = kapMeasure?.factsheet?.feature_base?.feature_values ?? []
  const hasFactsheetValidation = featureTypeConfig.has_kap_factsheet_validation

  // used for cross linking
  if (kapMeasure?.level === LEVEL.POLICY && kapMeasure?.factsheet?.level !== LEVEL.POLICY) {
    // remove features that are not compatible with the level of the factsheet from the selected features, as these should not be validated
    selectedFeatures = selectedFeatures.filter((feature) =>
      feature.feature_config.levels.includes(kapMeasure?.factsheet?.level ?? ''),
    )
  }

  const invalidFeatures = getInvalidSelectedFeatures(selectedFeatures, factsheetFeatures)

  const featureTypesWithInvalidFeatures = invalidFeatures.map((feature) => feature.feature_config.feature_type_id)

  return hasFactsheetValidation && featureTypesWithInvalidFeatures.includes(featureTypeConfig.id)
}

/**
 * Validates a feature selection compared to the feature selection in a factsheet.
 *
 * @param featureTypeConfig feature type config data for the feature type that contains the feature
 * @param kapMeasure KAP measure that is related to the feature base of the feature being validated, used to get the factsheet and its features
 * @param featureConfig feature config data for the feature being validated
 * @returns true if the feature is invalid
 */
const isFeatureSelectionInvalid = (
  featureTypeConfig: Feature_Type_Config | undefined,
  kapMeasure: Maybe<Kap_Measure> | undefined,
  featureConfig: Feature_Config,
): boolean => {
  const hasFactsheetValidation = featureTypeConfig?.has_kap_factsheet_validation ?? false
  const factsheetFeatures = kapMeasure?.factsheet?.feature_base?.feature_values ?? []

  const featureIsValid = factsheetFeatures
    ?.map((featureValue) => featureValue.feature_config.id)
    .includes(featureConfig.id)

  // used for cross linking
  if (kapMeasure?.level === LEVEL.POLICY && kapMeasure?.factsheet?.level !== LEVEL.POLICY) {
    const levelIsCompatible = featureConfig.levels.includes(kapMeasure?.factsheet?.level ?? '')
    // if level is of feature is not compatible with factsheet level, its valid since we don't check it
    return levelIsCompatible && hasFactsheetValidation && !featureIsValid
  }

  return hasFactsheetValidation && !featureIsValid
}

export { getInvalidSelectedFeatures, isFeatureSelectionInvalid, isFeatureTypeWithInvalidSelections }
